import { Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { MomentFormMoment } from "../../../moment/types/moment-form-moment";
import { OffsetSelector } from "../offset-selector";
import { DaySelectorV2 } from "./day-selector-v2";
import { ScheduleRepeatToggle } from "./schedule-repeat-toggle";
import { ScheduleTimezoneSelector } from "./schedule-timezone-selector";
import { SendAt } from "./send-at";

type ScheduleFormV2LayoutProps = {
  disabled: boolean;
  showValidationErrors: boolean;
};

export const ScheduleFormV2Layout: FC<ScheduleFormV2LayoutProps> = ({
  disabled,
  showValidationErrors,
}) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  useWatch({ control, name: ["schedule.event", "schedule.event_repeat"] });
  const [event] = getValues(["schedule.event"]);

  const { calendar, repeat: event_repeat, allow_repeats } = event ?? {};

  const automated = !calendar;

  const event_can_repeat = allow_repeats && event_repeat;

  return (
    <Stack sx={{ mt: 2 }} spacing={2} direction="column" alignItems="flex-start">
      <Controller
        name="schedule.interval_json"
        control={control}
        render={({ field }) => (
          <OffsetSelector
            sx={{ paddingLeft: 1 }}
            value={field.value}
            onChangeInterval={field.onChange}
            disabled={disabled}
          />
        )}
      />
      {!!event_can_repeat && <ScheduleRepeatToggle disabled={automated ? true : disabled} />}
      {(automated || event_can_repeat) && (
        <Controller
          name="schedule.on_event_key"
          control={control}
          render={({ field, fieldState }) => (
            <DaySelectorV2
              sx={{ paddingLeft: 1 }}
              on_event_key={field.value}
              onUpdateEventKeys={field.onChange}
              helperText={showValidationErrors ? fieldState.error?.message : undefined}
              disabled={disabled}
              disable_specific={automated && !!event_can_repeat}
            />
          )}
        />
      )}
      <Typography sx={{ pl: 1 }} variant="subtitle1">
        Send Communication at:
      </Typography>
      <Stack direction="row" spacing={2}>
        <SendAt disabled={disabled} showValidationErrors={showValidationErrors} />
        <ScheduleTimezoneSelector disabled={disabled} />
      </Stack>
    </Stack>
  );
};
